/**
 * Core stabilized metric scheduler.
 *
 * Runs a requestAnimationFrame loop, polling a probe function until the
 * returned FrameState is stable for N consecutive frames. Reports success
 * or failure (timeout, invalid start time, probe error) via ReliabilityMetrics.
 */
import { type MetricNameValue, ReliabilityMetrics } from '@canvas/frontend-metrics';
import type { CanvasMetadata } from '@canvas/frontend-metrics/reliability-metrics';

import { buildDatabaseSizeMetadata } from './build-database-size-metadata';

const DEFAULT_MAX_WAIT_MS = 120_000;
const DEFAULT_STABLE_FRAMES_REQUIRED = 2;
const START_TIME_FUTURE_SLACK_MS = 50;
const PROBE_ERROR_MESSAGE_MAX = 500;

/**
 * State returned by a probe function each frame.
 *
 * `done: true` with a consistent `stabilityKey` across N frames triggers success.
 */
export interface FrameState {
  done: boolean;
  stabilityKey: string | null;
}

/**
 * Parameters for the stabilized scheduler.
 */
export interface SchedulerParams {
  /**
   * Start of the measured interval (`performance.now()`). Clamped to the scheduler's
   * anchor time when slightly in the future; invalid values fail before the rAF loop runs.
   */
  startTime: number;
  /** Reliability metric name used for success and failure reports. */
  metricName: MetricNameValue;
  /**
   * Invoked every animation frame until success, timeout, or probe error. Must be cheap;
   * see {@link FrameState} for how `done` and `stabilityKey` gate completion.
   */
  getFrameState: () => FrameState;
  /** Merged into ReliabilityMetrics payloads for both success and failure. */
  metadata?: CanvasMetadata;
  /**
   * Maximum elapsed ms from the effective `startTime` before reporting
   * `stabilized-metric-timeout`. Defaults to {@link DEFAULT_MAX_WAIT_MS}.
   */
  maxWaitMs?: number;
  /**
   * Number of consecutive frames where `getFrameState` returns `done: true` and the same
   * non-null `stabilityKey` before reporting success. Defaults to
   * {@link DEFAULT_STABLE_FRAMES_REQUIRED}.
   */
  stableFramesRequired?: number;
  /**
   * When set, skips the rAF loop and records an immediate failure whose `errorMessage`
   * is `reason`.
   */
  failImmediately?: { reason: string };
}

/**
 * Mutable ref holding the current rAF ID; used to cancel in-flight metrics.
 */
export type MetricRef = { current: { rafId: number } | null };

/**
 * Creates a new MetricRef for tracking an in-flight metric.
 */
export function createMetricRef(): MetricRef {
  return { current: null };
}

/**
 * Cancels an in-flight metric by clearing its rAF.
 */
export function cancelMetric(ref: MetricRef): void {
  const token = ref.current;

  if (!token) {
    return;
  }

  cancelAnimationFrame(token.rafId);
  ref.current = null;
}

function resolveEffectiveStartTime(
  rawStart: number,
  anchorTime: number
): { ok: true; effectiveStart: number } | { ok: false; reason: string } {
  if (!Number.isFinite(rawStart)) {
    return { ok: false, reason: 'stabilized-metric-invalid-start-time' };
  }

  if (rawStart > anchorTime + START_TIME_FUTURE_SLACK_MS) {
    return { ok: false, reason: 'stabilized-metric-start-time-in-future' };
  }

  return { ok: true, effectiveStart: rawStart > anchorTime ? anchorTime : rawStart };
}

function truncateForMetadata(message: string): string {
  if (message.length <= PROBE_ERROR_MESSAGE_MAX) {
    return message;
  }

  return `${message.slice(0, PROBE_ERROR_MESSAGE_MAX)}…`;
}

/**
 * Starts the stabilized metric rAF loop.
 *
 * Polls `getFrameState` each frame. Once `done: true` with the same `stabilityKey`
 * for `stableFramesRequired` frames, reports success. Times out after `maxWaitMs`.
 */
export function runStabilizedScheduler(params: SchedulerParams, ref: MetricRef): void {
  cancelMetric(ref);

  const anchorTime = performance.now();
  const {
    startTime: rawStartTime,
    metricName,
    getFrameState,
    metadata,
    maxWaitMs = DEFAULT_MAX_WAIT_MS,
    stableFramesRequired = DEFAULT_STABLE_FRAMES_REQUIRED,
    failImmediately,
  } = params;

  const timing = resolveEffectiveStartTime(rawStartTime, anchorTime);

  const mergePublishMetadata = () => ({
    ...metadata,
    ...buildDatabaseSizeMetadata(metadata),
  });

  if (!timing.ok) {
    const failMeta = { ...mergePublishMetadata(), errorMessage: timing.reason };

    void ReliabilityMetrics.getInstance().failureWithDuration(
      metricName,
      anchorTime,
      anchorTime,
      failMeta
    );

    return;
  }

  const startTime = timing.effectiveStart;

  if (failImmediately) {
    const endTime = performance.now();
    const failMeta = { ...mergePublishMetadata(), errorMessage: failImmediately.reason };

    void ReliabilityMetrics.getInstance().failureWithDuration(
      metricName,
      startTime,
      endTime,
      failMeta
    );

    return;
  }

  let stableStreak = 0;
  let lastStabilityKey: string | null = null;

  const finishSuccess = () => {
    const endTime = performance.now();
    const baseMeta = mergePublishMetadata();

    void ReliabilityMetrics.getInstance().successWithDuration(
      metricName,
      startTime,
      endTime,
      baseMeta
    );
  };

  const finishTimeout = () => {
    const endTime = performance.now();
    const failMeta = {
      ...mergePublishMetadata(),
      errorMessage: 'stabilized-metric-timeout',
    };

    void ReliabilityMetrics.getInstance().failureWithDuration(
      metricName,
      startTime,
      endTime,
      failMeta
    );
  };

  const finishProbeError = (error: unknown) => {
    const endTime = performance.now();
    const detail = error instanceof Error ? error.message : String(error);
    const failMeta = {
      ...mergePublishMetadata(),
      errorMessage: `stabilized-metric-probe-error:${truncateForMetadata(detail)}`,
    };

    void ReliabilityMetrics.getInstance().failureWithDuration(
      metricName,
      startTime,
      endTime,
      failMeta
    );
  };

  const tick = () => {
    const elapsed = performance.now() - startTime;

    if (elapsed > maxWaitMs) {
      ref.current = null;

      finishTimeout();

      return;
    }

    let frameState: FrameState;

    try {
      frameState = getFrameState();
    } catch (error) {
      ref.current = null;

      finishProbeError(error);

      return;
    }

    const { done, stabilityKey } = frameState;

    if (done && stabilityKey !== null) {
      if (lastStabilityKey === stabilityKey) {
        stableStreak += 1;
      } else {
        stableStreak = 1;
        lastStabilityKey = stabilityKey;
      }

      if (stableStreak >= stableFramesRequired) {
        ref.current = null;

        finishSuccess();

        return;
      }
    } else {
      stableStreak = 0;
      lastStabilityKey = null;
    }

    ref.current = { rafId: requestAnimationFrame(tick) };
  };

  ref.current = { rafId: requestAnimationFrame(tick) };
}
